import type { VariantProps } from 'class-variance-authority';

export interface PaginationContext {
  currentPage: Ref<number>;
  totalPages: Ref<number>;
  itemType: Ref<string>;
  itemTypePlural: Ref<string>;
  pageQueryKey: Ref<string>;
  currentPageSize: Ref<number>;
  defaultPageSize: number;
  pageSizeQueryKey: Ref<string>;
  pageSizeOptions: Ref<number[]>;
  latestClickedButton: Ref<HTMLElement | null>;
  paginationCausedLoading: Ref<boolean>;
  totalItems: Ref<number>;
  displayPages: ComputedRef<PageButton[]>;
  paginationId: Ref<string>;

  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  firstPage: () => void;
  lastPage: () => void;
  setPageSize: (size: number) => void;
  setLatestClickedButton: (element: HTMLElement) => void;

  isFirstDisabled: Ref<boolean>;
  isPrevDisabled: Ref<boolean>;
  isNextDisabled: Ref<boolean>;
  isLastDisabled: Ref<boolean>;

  showFirstAndLastButtons: boolean;
  allowPageSizeChange: boolean;
}

export const PaginationContextKey: InjectionKey<PaginationContext> = Symbol('PaginationContext');

export type PaginationPayload = { providerKey: string };

export type EmitPageChange = Expand<PaginationPayload> & { page: number; pageSize: number; tags?: string };
export type EmitPageSizeChange = Expand<PaginationPayload> & { size: number; tags?: string };

export type PageButton = {
  number: number;
  label: string;
  key: string;
  disabled: boolean;
};

export type PaginationButtonVariants = VariantProps<typeof paginationButton>;

export interface PaginationTheme {
  button: typeof paginationButton;
  list: typeof paginationList;
  item: typeof paginationItem;
  pageSize: typeof paginationPageSize;
}

export type DefaultPaginationProps = {
  ariaLabel?: string;
};

export type LoadingType = 'filter' | 'page' | 'initial';

export type PaginatedListParams<T> = {
  path: string;
  pageSize: number;
  dataRef: Ref<T | null>;
  loadingStatusRef: Ref<PaginationLoadingObject[]>;
};

export type PaginationInitialStateOptions = {
  defaultPageSize?: number;
  initialPage?: number;
  initialPageSize?: number;
  pageQueryKey?: string;
  pageSizeQueryKey?: string;
};

export type PaginationLoadingObject = {
  type: LoadingType;
  state: boolean;
};
